import * as THREE from "three";
import { FlagEmblem, FlagFlourish } from "./flagEmblem";
import type { EmblemFlourish } from "./skins/looks";
import { KitClock } from "./skins/kit/clock";

/** Where a pennant's cloth is, in its mesh's frame (FlagFlourish.make measures it). */
export interface FlagFrame { readonly hoist: number; readonly fly: number; readonly yMin: number; readonly h: number; readonly zMid: number }

/**
 * How a banner's motes move and glow (skins/looks.ts EmblemFlourish): `life` seconds each; `rise` cloth heights over a
 * life (negative falls: snow and blossom start above the cloth and drift down past it); `sway` a sideways wander in
 * cloth heights; `rgb` the full-glow colour (additive, so it reads on the dark cloth); `cool` how far green fades over
 * a life (an ember reddens as it cools); `flare` a quick flare then a fade (embers, sparks) instead of an even fade in
 * and out (snow, blossom, chaff).
 */
export interface MoteStyle {
  readonly life: number; readonly rise: number; readonly sway: number;
  readonly rgb: readonly [number, number, number]; readonly cool: number; readonly flare: boolean;
}

export const MOTE_STYLES: Readonly<Partial<Record<EmblemFlourish, MoteStyle>>> = {
  embers: { life: 1.8, rise: 1.3, sway: 0, rgb: [1, 0.45, 0.12], cool: 0.5, flare: true },        // Ashen Banner
  sparks: { life: 1.4, rise: 1.6, sway: 0.05, rgb: [1, 0.8, 0.3], cool: 0.3, flare: true },       // Sunfire Banner
  snow: { life: 2.8, rise: -1.2, sway: 0.12, rgb: [0.82, 0.9, 1], cool: 0, flare: false },        // Frostfall Banner
  petals: { life: 2.6, rise: -1.0, sway: 0.2, rgb: [1, 0.5, 0.72], cool: 0, flare: false },       // Greenwake Banner
  chaff: { life: 2.4, rise: 0.8, sway: 0.16, rgb: [1, 0.78, 0.34], cool: 0, flare: false },       // Harvest Banner
  // MP-23: the Trophy Hall's banners
  leaves: { life: 2.9, rise: -1.1, sway: 0.22, rgb: [1, 0.84, 0.36], cool: 0, flare: false },     // Champion's Laurel: gold leaves fall
  forge: { life: 1.3, rise: 1.5, sway: 0.04, rgb: [0.78, 0.86, 1], cool: 0, flare: true },        // Iron Crown: cold steel sparks
  sand: { life: 2.0, rise: -1.4, sway: 0.03, rgb: [1, 0.86, 0.48], cool: 0, flare: false },       // Racer's Hourglass: sand falls
  stars: { life: 3.0, rise: 0.6, sway: 0.1, rgb: [0.75, 1, 0.96], cool: 0, flare: true },         // Wayfarer's banner: stars rise
};

/**
 * The banner's motes, a `Points` whose per-frame work is a METHOD: every fortress clone is made by Object3D.clone,
 * which builds a fresh instance of the class (keeping the method) but does not copy an `onBeforeRender` assigned to
 * the source — so each clone drives its own motes. The geometry is shared by the clones (written by whichever draws),
 * its frame rides in userData (copied with the clone). Drawn only while the worn emblem's flourish has a mote style and
 * motion is allowed (Reduced effects freezes them out: KitClock gates).
 */
export class FlagMotes extends THREE.Points {
  private seeds = Array.from({ length: FlagFlourish.Embers }, (_, i) => ({ t0: i / FlagFlourish.Embers, u: Math.random(), v: Math.random(), d: Math.random() - 0.5 }));

  override onBeforeRender(): void {
    KitClock.tick();                                   // the cloth's ripple and sheen run on the kit clock (flagCloth.ts)
    const f = FlagEmblem.wornLook()?.flourish;
    const style = f ? MOTE_STYLES[f] : undefined;
    const geo = this.geometry, frame = this.userData.flagFrame as FlagFrame | undefined;
    const on = !!frame && !!style && KitClock.gates().motion;
    geo.setDrawRange(0, on ? FlagFlourish.Embers : 0);
    if (!on || !frame || !style) return;
    const pos = geo.attributes.position.array as Float32Array, col = geo.attributes.color.array as Float32Array;
    const t = performance.now() / 1000, falls = style.rise < 0;
    this.seeds.forEach((s, i) => {
      const k = ((t / style.life + s.t0) % 1);
      if (k < 0.02) { s.u = Math.random(); s.v = Math.random(); s.d = Math.random() - 0.5; }   // a fresh mote
      const wander = style.sway * frame.h * Math.sin(k * Math.PI * 2 + s.u * 6);
      pos[i * 3] = frame.hoist + (frame.fly - frame.hoist) * (0.15 + 0.8 * s.u) + s.d * frame.h * 0.5 * k + wander;
      pos[i * 3 + 1] = frame.yMin + frame.h * (falls ? 0.7 + 0.6 * s.v : 0.2 + 0.7 * s.v) + frame.h * style.rise * k;
      pos[i * 3 + 2] = frame.zMid + s.d * frame.h * 0.2;
      // embers and sparks flare, then cool and fade; snow, blossom and chaff fade in and out evenly
      const glow = style.flare ? Math.sin(Math.PI * Math.min(1, k * 1.4)) * (1 - k) : Math.sin(Math.PI * k);
      col[i * 3] = style.rgb[0] * glow; col[i * 3 + 1] = style.rgb[1] * glow * (1 - style.cool * k); col[i * 3 + 2] = style.rgb[2] * glow;
    });
    geo.attributes.position.needsUpdate = true;
    geo.attributes.color.needsUpdate = true;
  }
}
